import { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle, KeyRound, Loader2 } from 'lucide-react';
import { usePasscode } from '../../context/PasscodeContext';
import { authAPI } from '../../services/api';

export default function PasscodeLockScreen() {
  const { passcodeLength, verifyPasscode, unlock, unlockDesktop, isNativePlatform } = usePasscode();

  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [password, setPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [shake, setShake] = useState(false);

  const hiddenInputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => hiddenInputRef.current?.focus(), 150);
  }, []);

  // Handle passcode input
  const handleInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, passcodeLength);
    setPasscode(value);
    setError('');

    if (value.length === passcodeLength) {
      setTimeout(() => handleUnlock(value), 150);
    }
  };

  const handleUnlock = (value) => {
    if (verifyPasscode(value)) {
      if (isNativePlatform) { unlock(); } else { unlockDesktop(); }
    } else {
      setError('Incorrect passcode');
      setAttempts(prev => prev + 1);
      setShake(true);
      setTimeout(() => {
        setShake(false);
        setPasscode('');
        hiddenInputRef.current?.focus();
      }, 500);
      if (attempts >= 2) setShowPasswordReset(true);
    }
  };

  const handlePasswordUnlock = async () => {
    if (!password.trim()) { setError('Please enter your password'); return; }
    setVerifyingPassword(true);
    setError('');
    try {
      await authAPI.verifyPassword({ password });
      if (isNativePlatform) { unlock(); } else { unlockDesktop(); }
    } catch (err) {
      setError('Incorrect password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  // Password reset view
  if (showPasswordReset) {
    return (
      <div className="fixed inset-0 bg-white z-[9999] flex flex-col items-center justify-center safe-area-top">
        <div className="w-full max-w-sm p-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-expense-100 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-10 h-10 text-expense-600" />
            </div>
            <h1 className="text-2xl font-bold text-surface-900">Forgot Passcode?</h1>
            <p className="text-surface-500 mt-2">Enter your account password to unlock</p>
          </div>
          <div className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your account password"
              className="w-full px-4 py-3.5 bg-surface-50 border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-lg"
              autoFocus
            />
            {error && (
              <div className="flex items-center gap-2 text-expense-600">
                <AlertCircle className="w-4 h-4" /><span className="text-sm">{error}</span>
              </div>
            )}
            <button
              onClick={handlePasswordUnlock}
              disabled={!password.trim() || verifyingPassword}
              className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {verifyingPassword ? (<><Loader2 className="w-5 h-5 animate-spin" />Verifying...</>) : 'Unlock'}
            </button>
            <button
              onClick={() => { setShowPasswordReset(false); setPassword(''); setError(''); setPasscode(''); setTimeout(() => hiddenInputRef.current?.focus(), 100); }}
              className="w-full py-3 text-surface-600 font-medium hover:text-surface-800 transition-colors"
            >
              Try passcode again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-white z-[9999] flex flex-col items-center justify-center safe-area-top"
      onClick={() => hiddenInputRef.current?.focus()}
    >
      <div className="w-full max-w-sm px-6">
        {/* Hidden input for native keyboard */}
        <input
          ref={hiddenInputRef}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          value={passcode}
          onChange={handleInput}
          className="absolute opacity-0 w-px h-px"
          style={{ left: '-9999px', top: '-9999px' }}
          autoComplete="off"
          autoFocus
        />

        {/* App icon */}
        <div className="flex flex-col items-center">
          <img src="/logo.png" alt="Hisab Journey" className="w-14 h-14 rounded-2xl mb-5 object-contain" />
          <h1 className="text-xl font-bold text-surface-900">Enter Passcode</h1>
          <p className="text-[13px] text-surface-400 mt-1">Unlock to access Hisab Journey</p>
        </div>

        {/* Dots */}
        <div className={`flex justify-center ${passcodeLength === 6 ? 'gap-2.5' : 'gap-3'} py-6 ${shake ? 'animate-shake' : ''}`}>
          {Array.from({ length: passcodeLength }).map((_, i) => (
            <div
              key={i}
              className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all ${
                i < passcode.length
                  ? 'border-primary-500 bg-primary-50'
                  : i === passcode.length
                  ? 'border-primary-400 bg-transparent'
                  : 'border-surface-200 bg-transparent'
              }`}
            >
              {i < passcode.length && (
                showPasscode
                  ? <span className="text-lg font-bold text-primary-600">{passcode[i]}</span>
                  : <div className="w-3 h-3 rounded-full bg-primary-500" />
              )}
            </div>
          ))}
        </div>

        {/* Show/Hide */}
        <button
          onClick={(e) => { e.stopPropagation(); setShowPasscode(!showPasscode); }}
          className={`flex items-center justify-center gap-1.5 mb-4 w-full ${showPasscode ? 'text-primary-600' : 'text-surface-400'}`}
        >
          {showPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          <span className="text-xs font-medium">{showPasscode ? 'Hide' : 'Show'}</span>
        </button>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 justify-center mb-4 text-expense-600">
            <AlertCircle className="w-4 h-4" /><span className="text-sm">{error}</span>
          </div>
        )}

        {/* Tap hint */}
        <p className="text-xs text-surface-400 text-center">Tap anywhere to open keyboard</p>

        {/* Forgot */}
        {attempts >= 3 && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowPasswordReset(true); }}
            className="w-full mt-6 py-3 text-primary-600 font-medium hover:text-primary-700 transition-colors"
          >
            Forgot passcode? Use password
          </button>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out; }
      `}</style>
    </div>
  );
}
